import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useColorStyle,
  useColorMode,
  SearchInput,
  Stack,
  Spinner,
  ModalCloseButton,
} from '@tonic-one/react';
import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrgStore } from '@Models/useOrgModel';
import { PolicyTypeEnum } from '@Policy/utils/constants';
import { PolicyHolderTypeEnum } from '@Policy/utils/types';
import ErrorAlert from '@Components/Common/ErrorAlert';
import { useApprovedSitesMutations, useApprovedSitesQuery } from './hooks';
import { useSharePointTargets } from './useSharePointTargets';
import Tree from '@Components/Tree';
import { TreeNode, Key } from '@Components/Tree/type';
import Dropdown from '@Components/Common/Dropdown';
import { FormLabel } from '@Components/Common/FormComponent';

interface AddSiteModalProps {
  isOpen: boolean;
  onClose: () => void;
  policyType: PolicyTypeEnum;
  currentSitesCount: number;
}

const MAX_SITES = 1024;

// Helper function to extract site name from SharePoint URL
const extractSiteNameFromUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;

    // For SharePoint URLs like: https://tenant.sharepoint.com/sites/sitename or
    // https://tenant.sharepoint.com/sites/product-showase/P-ABC/
    // Extract everything after /sites/ as the site path
    const sitesMatch = pathname.match(/\/sites\/(.+)/);
    if (sitesMatch) {
      // Remove trailing slash if present and return the full path
      return sitesMatch[1].replace(/\/$/, '');
    }

    // For personal sites like: https://tenant-my.sharepoint.com/personal/user_tenant_onmicrosoft_com
    // Return the raw path without parsing
    const personalMatch = pathname.match(/\/personal\/(.+)/);
    if (personalMatch) {
      return personalMatch[1].replace(/\/$/, '');
    }

    // For root sites or other patterns, try to get the last meaningful part
    const pathParts = pathname
      .split('/')
      .filter((part) => part && part !== 'sites');
    if (pathParts.length > 0) {
      return pathParts[pathParts.length - 1];
    }

    // Fallback to hostname if no meaningful path
    return urlObj.hostname.split('.')[0];
  } catch (error) {
    // If URL parsing fails, try to extract from the string directly
    const parts = url.split('/');
    const sitesIndex = parts.indexOf('sites');
    if (sitesIndex !== -1 && sitesIndex + 1 < parts.length) {
      // Get all segments after 'sites' and join them with /
      const siteSegments = parts.slice(sitesIndex + 1).filter((part) => part);
      if (siteSegments.length > 0) {
        return siteSegments.join('/');
      }
    }

    // Final fallback - return the original URL
    return url;
  }
};

// Helper function to flatten tree structure
const flattenTree = (nodes: TreeNode[]): TreeNode[] => {
  const result: TreeNode[] = [];

  const traverse = (node: TreeNode) => {
    result.push(node);
    if (node.children && node.children.length > 0) {
      node.children.forEach(traverse);
    }
  };

  nodes.forEach(traverse);
  return result;
};

// Helper function to mark existing sites as disabled in tree
const markExistingSitesAsDisabled = (
  nodes: TreeNode[],
  existingSiteUrls: Set<string>
): TreeNode[] => {
  return nodes.map((node) => {
    const nodeUrl = node?.data?.title || '';
    const isExisting = existingSiteUrls.has(nodeUrl);

    const updatedNode: TreeNode = {
      ...node,
      disabled: node.disabled || isExisting,
      disableCheckbox: node.disableCheckbox || isExisting,
    };

    if (node.children) {
      updatedNode.children = markExistingSitesAsDisabled(
        node.children,
        existingSiteUrls
      );
    }

    return updatedNode;
  });
};

const AddSiteModal = ({
  isOpen,
  onClose,
  policyType,
  currentSitesCount,
}: AddSiteModalProps) => {
  const { t } = useTranslation(['globalSetting', 'common']);
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { orgId, orgsMap } = useOrgStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedKeys, setSelectedKeys] = useState<Key[]>([]);
  const [selectedPolicyType, setSelectedPolicyType] = useState<string>('atp');
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);
  const [expandedKeys, setExpandedKeys] = useState<Key[]>(['all-sites']);

  // Debounce search term for 500ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Get current policy holder type for the data
  const currentPolicyHolderType =
    selectedPolicyType === 'atp'
      ? PolicyHolderTypeEnum.ATP
      : PolicyHolderTypeEnum.DLP;

  const { addSitesMutation } = useApprovedSitesMutations(policyType);

  // Use React Query hook to fetch SharePoint targets with backend search
  const { data: treeData = [], isLoading } = useSharePointTargets(
    orgId,
    isOpen,
    debouncedSearchTerm
  );

  // Fetch existing approved sites for the SharePoint service
  const { data: allExistingSites = [] } = useApprovedSitesQuery(policyType);

  // Filter existing sites by the selected policy holder type
  const existingSites = useMemo(() => {
    return allExistingSites.filter(
      (site) => site.policyType === currentPolicyHolderType
    );
  }, [allExistingSites, currentPolicyHolderType]);

  // Create a set of existing site URLs for quick lookup
  const existingSiteUrls = useMemo(() => {
    return new Set(existingSites.map((site) => site.siteUrl));
  }, [existingSites]);
  console.log(existingSiteUrls);

  // Reset expanded keys when modal opens
  useEffect(() => {
    if (isOpen) {
      setExpandedKeys(['all-sites']);
    }
  }, [isOpen]);

  // Reset selected keys when policy type changes to clear any disabled selections
  useEffect(() => {
    setSelectedKeys([]);
    setErrorMsg(undefined);
  }, [selectedPolicyType]);

  // Mark existing sites as disabled (search is now handled by backend)
  const filteredTreeData = useMemo(() => {
    return markExistingSitesAsDisabled(treeData, existingSiteUrls);
  }, [treeData, existingSiteUrls]);

  // Handle checkbox selection
  const handleCheck = (checkedKeys: Key[]) => {
    // Filter out parent nodes and special nodes, only keep leaf nodes
    const leafKeys = checkedKeys.filter((key) => {
      const isParent = key === 'all-sites';
      const isShowMore = key === 'show-more';
      return !isParent && !isShowMore;
    });

    // Check if we exceed the limit
    if (currentSitesCount + leafKeys.length > MAX_SITES) {
      setErrorMsg(
        t('globalSetting.approvedSites.addModal.error.maxSites', {
          count: MAX_SITES,
        })
      );
      return;
    }

    setSelectedKeys(leafKeys);
    setErrorMsg(undefined);
  };

  // Handle save
  const handleSave = async () => {
    if (selectedKeys.length === 0) {
      return;
    }

    try {
      setErrorMsg(undefined);

      // Get selected nodes data
      const allNodes = flattenTree(treeData);
      const selectedNodes = allNodes.filter(
        (node) => selectedKeys.includes(node.key) && node.isLeaf
      );

      const users = selectedNodes.map((node) => {
        const siteUrl = (node.title?.toString() as string) || '';
        return {
          name: extractSiteNameFromUrl(siteUrl), // Extracted site name
          companyGuid: orgId,
          policyType: currentPolicyHolderType,
          email: siteUrl, // Full site URL
        };
      });

      await addSitesMutation.mutateAsync(users);
      onClose();
    } catch (error: any) {
      setErrorMsg(error.message || t('common.apiException.internalError'));
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t('globalSetting.approvedSites.addModal.title')}
        </ModalHeader>
        <ModalBody>
          <Stack gap="4x">
            {errorMsg && (
              <Box>
                <ErrorAlert
                  errorMsg={errorMsg}
                  onClose={() => setErrorMsg(undefined)}
                />
              </Box>
            )}

            <Box>
              <Text fontSize="sm" color={colorStyle.color.secondary}>
                {t('globalSetting.approvedSites.addModal.description')}
              </Text>
            </Box>

            <Flex alignItems="center">
              <FormLabel
                label={t('globalSetting.approvedUser.column.policyType')}
                mr="2x"
              />
              <Dropdown
                options={['atp', 'dlp']}
                value={selectedPolicyType}
                onChange={setSelectedPolicyType}
                renderOption={(option) => {
                  return t(
                    `globalSetting.approvedUser.table.policyType.${option}`
                  );
                }}
                width={270}
              />
            </Flex>

            <Box>
              <Text>
                {t('common.label.currentOrganization')}: {orgsMap.get(orgId)}
              </Text>
            </Box>

            <Box>
              <SearchInput
                placeholder={t('log.button.search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClearInput={() => setSearchTerm('')}
                mb="2x"
                width="100%"
              />

              <Box
                position="relative"
                height="300px"
                overflow="hidden"
                border="1px solid"
                borderColor={colorStyle.divider}
                borderRadius="sm"
                p="2x"
              >
                {filteredTreeData.length === 0 && !isLoading ? (
                  <Flex
                    justifyContent="center"
                    alignItems="center"
                    height="100%"
                  >
                    <Text color={colorStyle.color.secondary}>
                      {t('common.noData')}
                    </Text>
                  </Flex>
                ) : (
                  <Tree
                    checkable
                    checkedKeys={selectedKeys}
                    onCheck={handleCheck}
                    defaultExpandedKeys={['all-sites']}
                    expandedKeys={expandedKeys}
                    onExpand={setExpandedKeys}
                    treeData={filteredTreeData}
                    showIcon={false}
                    style={{ height: '280px' }}
                    defaultExpandAll={false}
                    onSelect={(_: Key[], info: any) => {
                      // Handle "Show more" click
                      const node = info.node;
                      if (node && node.isShowMore && node.onSelect) {
                        node.onSelect();
                      }
                    }}
                  />
                )}

                {/* Loading overlay */}
                {isLoading && (
                  <Flex
                    position="absolute"
                    top="0"
                    left="0"
                    right="0"
                    bottom="0"
                    justifyContent="center"
                    alignItems="center"
                    bg={
                      colorMode === 'dark'
                        ? 'rgba(0, 0, 0, 0.7)'
                        : 'rgba(255, 255, 255, 0.8)'
                    }
                    borderRadius="sm"
                    zIndex="1"
                  >
                    <Spinner size="lg" />
                  </Flex>
                )}
              </Box>
            </Box>
          </Stack>
        </ModalBody>
        <ModalFooter gap="2x">
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={selectedKeys.length === 0 || addSitesMutation.isPending}
          >
            {t('common.button.save')}{' '}
            {selectedKeys.length > 0 && `(${selectedKeys.length})`}
          </Button>
          <Button onClick={onClose}>{t('common.button.cancel')}</Button>
        </ModalFooter>
        <ModalCloseButton />
      </ModalContent>
    </Modal>
  );
};

export default AddSiteModal;
